// Client for the Favorites (saved configurations) API — /api/favorites.
// Save / list / load configurations stored in the Postgres cpq2_saved_configurations
// table. Every saved config is public by default (2.1.1).

const API_BASE = "/api";

// Reads are retried on an AMBIGUOUS failure — a gateway 502/503/504 or a dropped
// connection. These come from the Static Web Apps front end when the managed
// Functions host is cold-starting or has just restarted (every deploy restarts
// it), not from the request being wrong: the same GET succeeds a moment later.
// Without this a single blip surfaced as "Load failed (HTTP 502)" and the user
// had to notice and click again. Only GETs use this, so retrying is always safe.
// A real rejection (400/404) is NOT retried — it would just fail again.
const RETRY_STATUSES = new Set([502, 503, 504]);
const READ_ATTEMPTS = 3;
const RETRY_DELAY_MS = 600;

async function getWithRetry(url: string): Promise<Response> {
  let lastErr: unknown = null;
  for (let attempt = 1; attempt <= READ_ATTEMPTS; attempt++) {
    try {
      const res = await fetch(url, { headers: { Accept: "application/json" } });
      if (!RETRY_STATUSES.has(res.status) || attempt === READ_ATTEMPTS) return res;
      // eslint-disable-next-line no-console
      console.warn(`[cpq2] favorites GET ${res.status} — retry ${attempt}/${READ_ATTEMPTS - 1}`);
    } catch (e) {
      lastErr = e;
      if (attempt === READ_ATTEMPTS) throw e;
      // eslint-disable-next-line no-console
      console.warn(`[cpq2] favorites GET failed — retry ${attempt}/${READ_ATTEMPTS - 1}`);
    }
    await new Promise((r) => setTimeout(r, RETRY_DELAY_MS * attempt));
  }
  throw lastErr ?? new Error("favorites request failed");
}

export type SavedConfigMeta = {
  id: number;
  name: string;
  notes: string | null;
  model: string | null;
  customer_id: string | null;
  customer_name: string | null;
  customer_part_number: string | null;
  origin_type: string | null;
  origin_tranid: string | null;
  created_by_id: string | null;
  created_by_name: string | null;
  is_public: boolean;
  created_at: string;
};

// A loaded record also carries the reloadable snapshot.
export type SavedConfig = SavedConfigMeta & { config_snapshot: string };

export type SaveFavoritePayload = {
  name: string;
  notes?: string | null;
  configSnapshot: string;
  model?: string | null;
  customerId?: string | null;
  customerName?: string | null;
  customerPartNumber?: string | null;
  originType?: string | null;
  originTranid?: string | null;
  createdById?: string | null;
  createdByName?: string | null;
  /** Which configurator saved this (a products.json slug). Scopes the lists. */
  product?: string | null;
};

export async function saveFavorite(
  payload: SaveFavoritePayload
): Promise<{ ok: boolean; id: number }> {
  const res = await fetch(`${API_BASE}/favorites`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(payload),
  });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; id?: number; error?: string };
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `Save failed (HTTP ${res.status})`);
  }
  return body as { ok: boolean; id: number };
}

export type ListFavoritesParams = {
  scope: "mine" | "all";
  createdBy?: string | null;
  /**
   * Limit the list to one configurator (a products.json slug). A question CODE is
   * only unique within a product, so another product's configuration is not just
   * noise — it cannot be loaded meaningfully. Omitting this returns every product's
   * rows, which is why both call sites pass it.
   */
  product?: string | null;
  q?: string;
  sort?: string;
  dir?: "asc" | "desc";
};

export async function listFavorites(params: ListFavoritesParams): Promise<SavedConfigMeta[]> {
  const qs = new URLSearchParams();
  qs.set("scope", params.scope);
  if (params.product) qs.set("product", params.product);
  if (params.createdBy) qs.set("createdBy", params.createdBy);
  if (params.q) qs.set("q", params.q);
  if (params.sort) qs.set("sort", params.sort);
  if (params.dir) qs.set("dir", params.dir);
  const res = await getWithRetry(`${API_BASE}/favorites?${qs.toString()}`);
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; items?: SavedConfigMeta[]; error?: string };
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `List failed (HTTP ${res.status})`);
  }
  return body.items ?? [];
}

export async function deleteFavorite(id: number): Promise<void> {
  const res = await fetch(`${API_BASE}/favorites?id=${encodeURIComponent(String(id))}`, {
    method: "DELETE",
    headers: { Accept: "application/json" },
  });
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; error?: string };
  if (!res.ok || body.ok === false) {
    throw new Error(body.error || `Delete failed (HTTP ${res.status})`);
  }
}

/**
 * Load one saved configuration.
 *
 * `product` is a GUARD, not a filter: the server refuses (409) a row saved in a
 * different configurator. A question CODE is only unique within a product, so
 * applying another product's snapshot would not fail loudly — it would land a pile
 * of answers that mean nothing here.
 */
export async function getFavorite(id: number, product?: string | null): Promise<SavedConfig> {
  const qs = new URLSearchParams({ id: String(id) });
  if (product) qs.set("product", product);
  const res = await getWithRetry(`${API_BASE}/favorites?${qs.toString()}`);
  const body = (await res.json().catch(() => ({}))) as { ok?: boolean; item?: SavedConfig; error?: string };
  if (!res.ok || body.ok === false || !body.item) {
    throw new Error(body.error || `Load failed (HTTP ${res.status})`);
  }
  return body.item;
}
